<script setup>

import {defineComponent} from "vue";
import ticker from "@/components/App/ticker.vue";
import headerDefault from "@/components/App/header-default/header-default.vue";
import footerDefault from "@/components/App/footer/footer.vue";
import {CommonMixin} from "@/mixins/commonMethods";
import {mapMutations} from "vuex";

import { useYandexMetrika } from 'yandex-metrika-vue3';
const metrika = useYandexMetrika();

import { ref, reactive, onMounted, onBeforeUnmount, watch, inject, computed, useTemplateRef, watchEffect } from 'vue';
import { useCommonComposable } from '@/composables/commonComposable.js';
import { useStore } from 'vuex';
import {onBeforeRouteLeave, useRoute, useRouter} from 'vue-router';
import { useHead } from "@vueuse/head";
import axios from "axios";
import NoAccount from "@/views/App/Checkout/components/no-account.vue";
import order from "@/store/modules/order";

const router = useRouter();
const route = useRoute();
const store = useStore();

const orderAddress = ref({});

const isFillNoAccount = ref(true);
const promocodesList = ref([]);
const promocode = ref('');
const promocodeNotification = ref({type: 'unsuccessful', text: ''});

const notify = inject('notify');
const { getCaseByNumeral, setImageThumbURL, user, getUser, isMobile } = useCommonComposable();

useHead({
    title: "Оформление заказа — Baller | Оригинальные джерси",
    meta: [
        {name: 'description', content: "Описание страницы"}
    ],
});

const cartItemsWithImages = reactive([]);

const cart = computed(() => store.state.cart);
const buyNow = ref(store.state.buyNow);
const paymentMethods = computed(() => store.state.activePaymentMethods);
const activeMethod = computed(() => {
    for (let elem of paymentMethods.value) {
        if (elem.default) {
            return elem;
        }
    }
});

async function getItemImages(code) {
    try {
        const fetchParams = { productCode: code };
        await store.dispatch('fetchImages', fetchParams);
        return store.state.images.filter((img) => img['Тип'] === 'Заглавная');
    } catch (err) {
        console.error('Error fetching images:', err);
    }
}

async function removeFromCart(item) {
    store.commit('REMOVE_FROM_CART', { item });

    let findPromocode = promocodesList.value.find(item => item.promocode === promocode.value && item.promocode !== 'Нет промокода');
    store.commit('CALCULATE_SUM', {discount: findPromocode['discount'] ? findPromocode['discount'] : 0});
}

function setBuyNow() {
    store.commit('SET_BUY_NOW', []);
}

async function placeOrder() {
    await addOrder();
    localStorage.setItem('OrderProducts', JSON.stringify(cartItemsWithImages));

    const response = await axios.post(process.env.VUE_APP_API_URL + '/orders/redirectToPayment', {
        orderId: localStorage.getItem('OrderNumber'),
        amount: cart.value.total * 100,
        orderDescription: 'Baller — магазин оригинальных джерси',
        orderItems: localStorage.getItem('OrderProducts'),
        userEmail: user.value[0].email || ''
    })

    if (response.data) {
        metrika.reachGoal('placeOrder');
        localStorage.setItem('PaymentId', JSON.parse(response.data)['PaymentId'])
        window.location.href = JSON.parse(response.data)["PaymentURL"]
    }
}

const checkArchivedItem = () => {
    if (cartItemsWithImages.length > 0) {
        for (let item of cartItemsWithImages) {
            if (item["Статус"] !== "Active") {
                store.commit('REMOVE_FROM_CART', { item })
                router.go(-1)
                notify('К сожалению, товар, который вы пытаетесь купить, уже продан')
            }
        }
    }
}

async function addOrder() {
    const config = { headers: { 'Cache-Control': 'no-cache' }, withCredentials: true };
    const data = {
        customerEmail: user.value[0].email,
        deliveryAddress: orderAddress.value["Имя адреса"],
        paymentMethod: 'Т-Банк',
        promocode: 'Нет промокода'
    };

    try {
        const response = await axios.post(process.env.VUE_APP_API_URL + "/orders/addOrder", data, config);
        await addOrderItem(response.data.orderNumber)
        // store.commit('order/SET_ORDER_NUMBER', response.data.orderNumber);
        localStorage.setItem('OrderNumber', response.data.orderNumber);
    } catch (error) {
        console.log(error);
    }
}

async function addOrderItem(orderNumber) {
    for (let item of cartItemsWithImages) {
        const data = {
            productCode: item['Код'],
            orderNumber: orderNumber || 0,
            quantity: 1,
            statusName: 'Archived'
        };

        if (data.productCode === 'Shipping') return;

        try {
            await axios.post(process.env.VUE_APP_API_URL + "/orders/addOrderItem", data);
        } catch (error) {
            console.log(error);
        }
    }
}

const addPromocode = async () => {
    const params = {promocode: promocode.value}
    const baseUrl = process.env.VUE_APP_API_URL + "";
    const res = await axios.post(baseUrl + "/promocodes/checkPromocode", params);

    let findPromocode = ''
    if (res.data) {
        findPromocode = res.data
    }

    const activePromocode = JSON.parse(localStorage.getItem('activePromocode'))

    if (activePromocode && activePromocode.name !== promocode.value) {
        store.commit('CALCULATE_SUM', {discount: 0})
    }

    if (activePromocode && activePromocode.name === promocode.value) {
        promocodeNotification.value['type'] = 'unsuccessful';
        promocodeNotification.value['text'] = `Промокод «${promocode.value}» уже был применен`;
        return
    }

    if (findPromocode) {
        const discount = findPromocode['discount'] < 1 ? findPromocode['discount'] * 100 + '%' : findPromocode['discount'] + '₽';
        promocodeNotification.value['type'] = 'success';
        promocodeNotification.value['text'] = `Промокод «${promocode.value}» применён успешно — скидка ${discount}`;

        localStorage.setItem('activePromocode', JSON.stringify({name: promocode.value, discount: discount}));

        store.commit('CALCULATE_SUM', {discount: findPromocode['discount']});
    } else {
        promocodeNotification.value['type'] = 'unsuccessful';
        promocodeNotification.value['text'] = `Промокод «${promocode.value}» не существует или уже истёк`;

        localStorage.setItem('activePromocode', null);

        store.commit('CALCULATE_SUM', {discount: 0})
    }
}

const fillNoAccount = async () => {
    isFillNoAccount.value = true;
    if (user.value === null) {
        await getUser();
    }
    await store.dispatch('address/fetchUserAddresses', { customerId: user.value[0].ID }); // Загружаем адреса текущего пользователя
    orderAddress.value = JSON.parse(JSON.stringify(store.state.address.userAddresses)).find((item) => item["Статус"] === "Активен");

    await getShippingCost()
}

const getShippingCost = async () => {
    if (cartItemsWithImages.length < 1) {
        store.commit('RESET_CART')
        return
    }

    if (!orderAddress.value['Имя адреса']) {
        return
    }

    const data = {
        destination: {"platform_station_id": orderAddress.value['Яндекс ID']},
        tariff: 'self_pickup',
        total_weight: 1000
    }

    const response = await axios.post(process.env.VUE_APP_API_URL + "/orders/getShippingCost", data);
    if (response.data) {
        orderAddress.value['Стоимость доставки'] = (parseInt(JSON.parse(response.data).pricing_total) + 100) + '₽'
        orderAddress.value['Срок доставки'] = (JSON.parse(response.data).delivery_days+2) + ' ' + getDayByNumeral(JSON.parse(response.data).delivery_days + 2)

        if (!buyNow.value['Код'] && cartItemsWithImages[0]['Код'] === 'Shipping') {
            store.commit('REMOVE_FROM_CART', {item: {'Код': ''}})
        }

        if (buyNow.value['Код']) {
            cartItemsWithImages.push({'Код': 'Shipping','Название продукта': 'Доставка', 'Цена по скидке': (parseInt(JSON.parse(response.data).pricing_total)+100)})
            cart.value.total += (parseInt(JSON.parse(response.data).pricing_total)+100)
        } else if (cartItemsWithImages.length > 0 && cartItemsWithImages[0]['Код'] !== 'Shipping') {
            store.commit('ADD_TO_CART', {
                'item': {
                    'Код': 'Shipping',
                    'Название продукта': 'Доставка',
                    'Цена по скидке': (parseInt(JSON.parse(response.data).pricing_total) + 100)
                }, quantity: 1
            })
        }
    }
}

const getDayByNumeral = (num) => {
    if (num > 20) {
        let array = num.toString().split('');
        switch (array[array.length - 1].toLowerCase()) {
            case 1: return 'день';
            case 2: return 'дня';
            case 3: return 'дня';
            case 4: return 'дня';
            default: return 'дней';
        }
    } else {
        switch (num) {
            case 1: return 'день';
            case 2: return 'дня';
            case 3: return 'дня';
            case 4: return 'дня';
            default: return 'дней';
        }
    }
}

watch( () => cart.value.items.length, async (newValue, oldValue) => {
    if (oldValue !== newValue) {
        cartItemsWithImages.splice(0, cartItemsWithImages.length);
        if (cart.value.items) {
            for (let item of cart.value.items) {
                const images = await getItemImages(item['Код']);
                if (images.length > 0) {
                    item['Ссылка'] = images[0]['Ссылка'];
                }
                cartItemsWithImages.push(item);
            }
        }
    }
}, { deep: true });

onMounted(async () => {
    window.addEventListener('beforeunload', setBuyNow);

    cartItemsWithImages.splice(0, cartItemsWithImages.length);
    for (let item of cart.value.items) {
        const images = await getItemImages(item['Код']);
        if (images.length > 0) {
            item['Ссылка'] = images[0]['Ссылка'];
        }
        cartItemsWithImages.push(item);
    }

    if (buyNow.value['Код']) {
        cartItemsWithImages.splice(0, cartItemsWithImages.length);

        const images = await getItemImages(buyNow.value['Код']);
        if (images.length > 0) {
            buyNow.value['Ссылка'] = images[0]['Ссылка'];
        }

        cartItemsWithImages.push(buyNow.value);
    }

    await getUser(true);
    if (user.value === null) {
        isFillNoAccount.value = false;
    } else {
        await store.dispatch('address/fetchUserAddresses', { customerId: user.value[0].ID }); // Загружаем адреса текущего пользователя
        orderAddress.value = JSON.parse(JSON.stringify(store.state.address.userAddresses)).find((item) => item["Статус"] === "Активен");
    }

    await axios.get(process.env.VUE_APP_API_URL + '/promocodes/getPromocodes', { headers: { 'Cache-Control': 'no-cache' }});
    store.commit('CALCULATE_SUM', {discount: 0});
    localStorage.setItem('activePromocode', null);

    await getShippingCost()
});

onBeforeUnmount(() => {
    window.removeEventListener('beforeunload', setBuyNow);
});

// Навигационные хуки

onBeforeRouteLeave((to, from, next) => {
    setBuyNow();
    next()
});
</script>

<template>
    <div class="silver-background">
        <ticker></ticker>
        <header-default></header-default>
        <div class="container page column-page">
            <div class="row">
                <h3 class="page-header">Оформление заказа</h3>
            </div>
            <div class="row order-container">
                <div class="col-lg-8 col-md-8 col-12">
                    <div class="order-elems" v-if="cartItemsWithImages.length > 0 && cartItemsWithImages[0]['Название продукта']">
                        <h4 class="block-header">Список товаров</h4>
                        <div class="hr"></div>
                        <div v-for="item of cartItemsWithImages">
                            <div v-if="item['Код'] === 'Shipping'" id="shipping">
                                <svg width="20" height="22" viewBox="0 0 20 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M9.029 1.25153L8.5435 0.37729L8.54345 0.37732L9.029 1.25153ZM10 1V0V1ZM10.971 1.25153L11.4566 0.37732L11.4565 0.37729L10.971 1.25153ZM18.486 5.42553L18.972 4.55157L18.9716 4.55132L18.486 5.42553ZM19 6.29953L18 6.29949V6.29953H19ZM19 14.5345H18V14.5348L19 14.5345ZM17.971 16.2835L17.4855 15.4093L17.4854 15.4093L17.971 16.2835ZM10.971 20.1715L11.4565 21.0458L11.4566 21.0457L10.971 20.1715ZM9.029 20.1715L8.54335 21.0457L8.5435 21.0458L9.029 20.1715ZM2.029 16.2825L2.51465 15.4084L2.5145 15.4083L2.029 16.2825ZM1 14.5355H-2.38419e-07L2.98023e-07 14.5363L1 14.5355ZM1 6.29953H2V6.29949L1 6.29953ZM1.514 5.42553L1.02845 4.55132L1.028 4.55157L1.514 5.42553ZM5.01436 7.33737C4.53157 7.60558 4.35763 8.21439 4.62584 8.69717C4.89406 9.17995 5.50286 9.3539 5.98564 9.08568L5.5 8.21153L5.01436 7.33737ZM14.9856 4.08568C15.4684 3.81747 15.6424 3.20867 15.3742 2.72588C15.1059 2.2431 14.4971 2.06916 14.0144 2.33737L14.5 3.21153L14.9856 4.08568ZM4.48683 10.166C4.00441 9.89716 3.39537 10.0703 3.1265 10.5527C2.85763 11.0351 3.03075 11.6442 3.51317 11.913L4 11.0395L4.48683 10.166ZM6.51317 13.585C6.99559 13.8539 7.60463 13.6808 7.8735 13.1984C8.14237 12.7159 7.96925 12.1069 7.48683 11.838L7 12.7115L6.51317 13.585ZM9.029 1.25153L9.5145 2.12576C9.66302 2.04328 9.83011 2 10 2V1V0C9.49033 0 8.98907 0.129847 8.5435 0.37729L9.029 1.25153ZM10 1V2C10.1699 2 10.337 2.04328 10.4855 2.12576L10.971 1.25153L11.4565 0.37729C11.0109 0.129847 10.5097 0 10 0V1ZM10.971 1.25153L10.4854 2.12573L18.0004 6.29973L18.486 5.42553L18.9716 4.55132L11.4566 0.37732L10.971 1.25153ZM18.486 5.42553L18 6.29949V6.29949L18.862 5.79255L19.724 5.28562C19.5432 4.97828 19.2836 4.72485 18.972 4.55157L18.486 5.42553ZM18.862 5.79255L18 6.29949V6.29949L19 6.29953L20 6.29957C20 5.94302 19.9047 5.59295 19.724 5.28562L18.862 5.79255ZM19 6.29953H18V14.5345H19H20V6.29953H19ZM19 14.5345L18 14.5348C18 14.7132 17.9524 14.8884 17.8619 15.0421L18.7238 15.5492L19.5857 16.0563C19.8571 15.595 20.0001 15.0695 20 14.5343L19 14.5345ZM18.7238 15.5492L17.8619 15.0421C17.7714 15.1959 17.6415 15.3227 17.4855 15.4093L17.971 16.2835L18.4565 17.1578C18.9244 16.8979 19.3143 16.5176 19.5857 16.0563L18.7238 15.5492ZM17.971 16.2835L17.4854 15.4093L10.4854 19.2973L10.971 20.1715L11.4566 21.0457L18.4566 17.1577L17.971 16.2835ZM10.971 20.1715L10.4855 19.2973C10.337 19.3798 10.1699 19.4231 10 19.4231V20.4231V21.4231C10.5097 21.4231 11.0109 21.2932 11.4565 21.0458L10.971 20.1715ZM10 20.4231V19.4231C9.83011 19.4231 9.66303 19.3798 9.5145 19.2973L9.029 20.1715L8.5435 21.0458C8.98907 21.2932 9.49033 21.4231 10 21.4231V20.4231ZM9.029 20.1715L9.51465 19.2974L2.51465 15.4084L2.029 16.2825L1.54335 17.1567L8.54335 21.0457L9.029 20.1715ZM2.029 16.2825L2.5145 15.4083C2.35868 15.3218 2.22881 15.1951 2.13836 15.0416L1.27671 15.5491L0.415072 16.0566C0.686449 16.5173 1.07604 16.8972 1.5435 17.1568L2.029 16.2825ZM1.27671 15.5491L2.13836 15.0416C2.0479 14.888 2.00013 14.713 2 14.5348L1 14.5355L2.98023e-07 14.5363C0.000394285 15.071 0.143696 15.5959 0.415072 16.0566L1.27671 15.5491ZM1 14.5355H2V6.29953H1H0V14.5355H1ZM1 6.29953L2 6.29949L2 6.29949L1.13802 5.79255L0.27603 5.28562C0.0952854 5.59295 -1.46627e-05 5.94302 0 6.29957L1 6.29953ZM1.13802 5.79255L2 6.29949V6.29949L1.514 5.42553L1.028 4.55157C0.716393 4.72485 0.456774 4.97828 0.27603 5.28562L1.13802 5.79255ZM1.514 5.42553L1.99955 6.29973L9.51455 2.12573L9.029 1.25153L8.54345 0.37732L1.02845 4.55132L1.514 5.42553ZM1 5.71153L0.514357 6.58568L9.51436 11.5857L10 10.7115L10.4856 9.83737L1.48564 4.83737L1 5.71153ZM10 10.7115L10.4856 11.5857L19.4856 6.58568L19 5.71153L18.5144 4.83737L9.51436 9.83737L10 10.7115ZM10 10.7115H9V20.7115H10H11V10.7115H10ZM5.5 8.21153L5.98564 9.08568L14.9856 4.08568L14.5 3.21153L14.0144 2.33737L5.01436 7.33737L5.5 8.21153ZM4 11.0395L3.51317 11.913L6.51317 13.585L7 12.7115L7.48683 11.838L4.48683 10.166L4 11.0395Z" fill="black"/>
                                </svg>
                                <span v-if="!orderAddress || !isFillNoAccount">Не знаем, куда доставить ваш товар. <router-link :to="{name: 'settings'}">Добавить адрес</router-link></span>
                                <span v-else-if="!orderAddress['Стоимость доставки'] && orderAddress['Имя адреса']">Узнаём детали доставки...</span>
                                <span v-else><i>Доставка: {{ orderAddress['Стоимость доставки'] }}.</i> Приедет на адрес <router-link :to="{name: 'settings'}" tag="em">{{ orderAddress['Имя адреса'] }}</router-link> примерно за {{ orderAddress['Срок доставки'] }}</span>
                            </div>
                            <div v-else class="order-item">
                                <router-link :to="`/products/${item['Код']}`" class="item-info">
                                    <div class="item-img" :style="'background: url(' + setImageThumbURL(item) + ')'">
                                    </div>
                                    <div class="item-text">
                                        <div class="top">
                                            <p class="item-header">{{ item["Название продукта"] }}</p>
                                            <p class="item-size">Размер: {{ item["Размер"] }}</p>
                                        </div>
                                        <p class="item-price">{{ item["Цена по скидке"] }}&#8381;</p>
                                    </div>
                                </router-link>
                                <div class="delete-item" v-if="buyNow.length === 0" @click.prevent="removeFromCart(item)">
                                    <svg width="30" height="31" viewBox="0 0 30 31" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <rect y="0.5" width="30" height="30" rx="10" fill="#6D6F71"/>
                                        <path d="M13.75 15.5L10 11.75L11.25 10.5L15 14.25L18.75 10.5L20 11.75L16.25 15.5L20 19.25L18.75 20.5L15 16.75L11.25 20.5L10 19.25L13.75 15.5Z" fill="white"/>
                                    </svg>
                                </div>
                            </div>
                        </div>
                        <div class="hr"></div>
                        <div class="total-info" v-if="cart.items.length > 0 && buyNow.length === 0">
                            <span>{{ cart.totalQuantity }} {{ getCaseByNumeral(cart.totalQuantity, 'элемент') }}</span>
                            <span>|</span>
                            <span>Итого: {{ cart.total }}&#8381;</span>
                            <span v-if="cart.discount" class="discount-text">(-{{ cart.discount }})</span>
                        </div>
                        <div class="total-info" v-if="buyNow['Код']">
                            <span>1 элемент</span>
                            <span>|</span>
                            <span>Итого: {{ cart.total }}&#8381;</span>
                            <span v-if="cart.discount" class="discount-text">(-{{ cart.discount }})</span>
                        </div>
                    </div>
                    <div class="no-order-items-container" v-else>
                        <div class="no-order-items">
                            <span>Ваша корзина пуста</span>
                            <router-link :to="{path: '/products'}" class="action">>>> К покупкам</router-link>
                        </div>
                    </div>
                    <no-account v-if="!isFillNoAccount && !isMobile" @fill="fillNoAccount"></no-account>
                </div>
                <div class="col-lg-4 col-md-4 col-12">
                    <div class="container-card-block payment-methods">
                        <h4 class="block-header">Промокод</h4>
                        <div class="hr"></div>
                        <div class="content-row">
                            <input name="promocode" v-model="promocode" id="promocode" placeholder="Введите промокод">
                            <div id="enter-promocode" @click="addPromocode">
                                <svg width="10" height="12" viewBox="0 0 10 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path fill-rule="evenodd" clip-rule="evenodd" d="M6.99382e-07 7.33331L4 11.3333L4.94669 10.3866L2.55334 7.99997L10 7.99997L10 -2.87691e-05L8.66669 -2.90022e-05L8.66669 6.66663L2.55334 6.66663L4.94669 4.27997L4 3.33331L6.99382e-07 7.33331Z" fill="black"/>
                                </svg>
                            </div>
                        </div>
                        <div v-if="promocodeNotification['text']" :class="[promocodeNotification['type'] === 'success' ? 'success-notification' : '', 'promocode-notification']">
                            <span>{{ promocodeNotification['text'] }}</span>
                        </div>
                    </div>
                    <div class="container-card-block">
                        <h4 class="block-header">Способ оплаты</h4>
                        <div class="hr"></div>
                        <router-link class="method-elem" :to="method.link" v-for="method of paymentMethods">
                            <div class="method-image" v-html="method.icon">
                            </div>
                            <div class="method-label">
                                <span>{{ method.name_ru }}</span>
                            </div>
                        </router-link>
                    </div>
                    <div @click="placeOrder" :class="['pay-order', (cart.items.length === 0 && (!buyNow || buyNow.length < 1)) || !isFillNoAccount ? 'disabled' : '']">
                        <span>Оплатить {{ cart.total }}&#8381;</span>
                    </div>
                </div>
            </div>
            <no-account v-if="!isFillNoAccount && isMobile" @fill="fillNoAccount"></no-account>
        </div>
    </div>
    <footer-default></footer-default>
</template>

<style scoped lang="scss" src="./style.scss"></style>